// Pointer-Events drag-and-drop for task rows. Replaces the old HTML5
// DnD API so the same code path serves mouse, pen, and touch.
//
// Pickup gesture:
//   - mouse: pointerdown, then any movement past MOUSE_DRAG_THRESHOLD.
//   - touch / pen: pointerdown, then hold still for LONG_PRESS_MS. If
//     the user moves past TOUCH_CANCEL_THRESHOLD before the timer fires
//     we abort so the browser can take over (e.g. scroll the list).
//
// Tap → edit is preserved: a `pointerup` while still in the "pressing"
// state lets the synthetic `click` propagate to the row's existing
// edit-modal handler. After a successful drop we install a one-shot
// capture-phase `click` listener that swallows the next click so a
// drop never accidentally opens the edit dialog.
//
// On Android, long-press normally pops a text-selection callout; the
// `contextmenu` handler suppresses it while a press is in flight.

import type { Quadrant } from "../generated/Quadrant";
import { applyMove } from "../render/matrix";

const LONG_PRESS_MS = 350;
const MOUSE_DRAG_THRESHOLD = 5;
const TOUCH_CANCEL_THRESHOLD = 8;
const CLICK_SUPPRESS_MS = 300;

type Idle = { phase: "idle" };
type Pressing = {
  phase: "pressing";
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  sourceId: string;
  sourceQuadrant: Quadrant;
  longPressTimer: ReturnType<typeof setTimeout> | null;
};
type Dragging = {
  phase: "dragging";
  pointerId: number;
  pointerType: string;
  sourceId: string;
  sourceQuadrant: Quadrant;
  ghost: HTMLElement;
  ghostOffsetX: number;
  ghostOffsetY: number;
  currentTarget: DropTarget | null;
};
type State = Idle | Pressing | Dragging;

interface DropTarget {
  quadrant: Quadrant;
  beforeTaskId: string | null;
}

let state: State = { phase: "idle" };
let attached: HTMLElement | null = null;
let indicator: HTMLElement | null = null;
// Timestamp (ms) until which any task-row click should be ignored. Set
// after a drop; cleared on next pointerdown. Read by matrix.ts.
let suppressClicksUntil = 0;

/** True if a drag-drop just ended and the immediately-following synthetic
 *  click should be ignored. Used by `matrix.ts` to suppress the
 *  edit-modal click handler after a successful drop. */
export function isDragSuppressingClicks(): boolean {
  return performance.now() < suppressClicksUntil;
}

export function mountDragDrop(matrix: HTMLElement): void {
  unmountDragDrop();
  attached = matrix;
  matrix.addEventListener("pointerdown", onPointerDown);
  matrix.addEventListener("pointermove", onPointerMove);
  matrix.addEventListener("pointerup", onPointerUp);
  matrix.addEventListener("pointercancel", onPointerCancel);
  matrix.addEventListener("contextmenu", onContextMenu);
  // Non-passive touchmove: `preventDefault()` here claims the gesture
  // back from the browser's scroll/pan handling once we enter the
  // dragging phase. Without this, mobile Safari / Chrome commit to a
  // pan-y scroll after the long-press fires and stop delivering
  // pointermove events to JS, even with `setPointerCapture` active.
  matrix.addEventListener("touchmove", onTouchMove, { passive: false });
  document.addEventListener("keydown", onKeyDown);
}

export function unmountDragDrop(): void {
  if (attached) {
    attached.removeEventListener("pointerdown", onPointerDown);
    attached.removeEventListener("pointermove", onPointerMove);
    attached.removeEventListener("pointerup", onPointerUp);
    attached.removeEventListener("pointercancel", onPointerCancel);
    attached.removeEventListener("contextmenu", onContextMenu);
    attached.removeEventListener("touchmove", onTouchMove);
  }
  document.removeEventListener("keydown", onKeyDown);
  abort();
  attached = null;
  if (indicator?.parentNode) indicator.parentNode.removeChild(indicator);
  indicator = null;
}

function onTouchMove(e: TouchEvent): void {
  if (state.phase === "dragging") e.preventDefault();
}

function onPointerDown(e: PointerEvent): void {
  if (!e.isPrimary) return;
  // Pointer Events report button=0 for the primary mouse button and for
  // touch contacts; ignore right-click / middle-click etc.
  if (e.pointerType === "mouse" && e.button !== 0) return;

  const target = e.target;
  if (!(target instanceof Element)) return;
  const li = target.closest<HTMLElement>(".task[data-task-id]");
  if (!li) return;
  const sourceId = li.dataset.taskId;
  if (!sourceId) return;
  const quadrantEl = li.closest<HTMLElement>("[data-quadrant]");
  const sourceQuadrant = quadrantEl?.dataset.quadrant as Quadrant | undefined;
  if (!sourceQuadrant) return;

  abort();
  // Fresh interaction: clear any leftover post-drop suppression so a
  // genuine tap right after a drop still opens the edit dialog.
  suppressClicksUntil = 0;

  const isTouchLike = e.pointerType === "touch" || e.pointerType === "pen";
  const longPressTimer = isTouchLike
    ? setTimeout(() => beginDrag(e.clientX, e.clientY), LONG_PRESS_MS)
    : null;

  state = {
    phase: "pressing",
    pointerId: e.pointerId,
    pointerType: e.pointerType,
    startX: e.clientX,
    startY: e.clientY,
    sourceId,
    sourceQuadrant,
    longPressTimer,
  };
}

function onPointerMove(e: PointerEvent): void {
  if (state.phase === "pressing" && e.pointerId === state.pointerId) {
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    const dist = Math.hypot(dx, dy);
    if (state.pointerType === "mouse") {
      if (dist >= MOUSE_DRAG_THRESHOLD) beginDrag(e.clientX, e.clientY);
    } else if (dist >= TOUCH_CANCEL_THRESHOLD) {
      // Pre-timer movement on touch → user wants to scroll. Bail so the
      // browser can take the gesture.
      abort();
    }
    return;
  }
  if (state.phase === "dragging" && e.pointerId === state.pointerId) {
    e.preventDefault();
    moveGhost(e.clientX, e.clientY);
    updateDropTarget(e.clientX, e.clientY);
  }
}

function onPointerUp(e: PointerEvent): void {
  if (state.phase === "pressing" && e.pointerId === state.pointerId) {
    // Tap — let the synthetic click open the edit modal.
    abort();
    return;
  }
  if (state.phase === "dragging" && e.pointerId === state.pointerId) {
    const target = state.currentTarget;
    const sourceId = state.sourceId;
    teardownDrag();
    if (target) applyMove(sourceId, target.quadrant, target.beforeTaskId);
    suppressNextClick();
  }
}

function onPointerCancel(e: PointerEvent): void {
  if (state.phase === "idle") return;
  const active = state as Pressing | Dragging;
  if (active.pointerId !== e.pointerId) return;
  abort();
}

function onContextMenu(e: Event): void {
  if (state.phase !== "idle") e.preventDefault();
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === "Escape" && state.phase !== "idle") abort();
}

function beginDrag(x: number, y: number): void {
  if (state.phase !== "pressing") return;
  const sourceEl = findTaskEl(state.sourceId);
  if (!sourceEl) {
    abort();
    return;
  }
  if (state.longPressTimer !== null) clearTimeout(state.longPressTimer);

  // Capture the pointer on the matrix so we keep receiving events even
  // if the finger leaves the source row.
  if (attached) {
    try {
      attached.setPointerCapture(state.pointerId);
    } catch {
      // ignore — pointer may already be gone.
    }
  }

  sourceEl.classList.add("task--dragging");

  const rect = sourceEl.getBoundingClientRect();
  const ghost = sourceEl.cloneNode(true) as HTMLElement;
  ghost.classList.add("task__ghost");
  ghost.classList.remove("task--dragging", "task--being-edited");
  // Strip any inline editing-ring shadow so the floater stays clean.
  ghost.style.boxShadow = "";
  ghost.style.width = `${rect.width}px`;
  ghost.style.height = `${rect.height}px`;
  ghost.removeAttribute("id");
  document.body.appendChild(ghost);

  state = {
    phase: "dragging",
    pointerId: state.pointerId,
    pointerType: state.pointerType,
    sourceId: state.sourceId,
    sourceQuadrant: state.sourceQuadrant,
    ghost,
    ghostOffsetX: x - rect.left,
    ghostOffsetY: y - rect.top,
    currentTarget: null,
  };

  document.body.classList.add("body--dragging");
  moveGhost(x, y);
  updateDropTarget(x, y);
}

function moveGhost(x: number, y: number): void {
  if (state.phase !== "dragging") return;
  const left = x - state.ghostOffsetX;
  const top = y - state.ghostOffsetY;
  // Keep the rotation from CSS by composing it into the translate.
  state.ghost.style.transform = `translate(${left}px, ${top}px) rotate(1.2deg)`;
}

function updateDropTarget(x: number, y: number): void {
  if (state.phase !== "dragging") return;
  const next = resolveDropTarget(x, y, state.sourceId, state.ghost);
  state.currentTarget = next;
  renderIndicator(next);
}

function resolveDropTarget(
  x: number,
  y: number,
  sourceId: string,
  ghost: HTMLElement,
): DropTarget | null {
  // Hide the ghost so elementFromPoint reads the real DOM beneath it.
  const savedVis = ghost.style.visibility;
  ghost.style.visibility = "hidden";
  const el = document.elementFromPoint(x, y);
  ghost.style.visibility = savedVis;
  if (!(el instanceof Element)) return null;

  const quadrantEl = el.closest<HTMLElement>("[data-quadrant].quadrant");
  if (!quadrantEl) return null;
  const quadrant = quadrantEl.dataset.quadrant as Quadrant | undefined;
  if (!quadrant) return null;

  const list = quadrantEl.querySelector<HTMLElement>(".quadrant__list");
  if (!list) return { quadrant, beforeTaskId: null };

  const tasks = Array.from(
    list.querySelectorAll<HTMLElement>(".task[data-task-id]"),
  ).filter((t) => t.dataset.taskId !== sourceId);
  if (tasks.length === 0) return { quadrant, beforeTaskId: null };

  for (const t of tasks) {
    const rect = t.getBoundingClientRect();
    if (y < rect.top + rect.height / 2) {
      return { quadrant, beforeTaskId: t.dataset.taskId ?? null };
    }
  }
  return { quadrant, beforeTaskId: null };
}

function renderIndicator(target: DropTarget | null): void {
  if (!target) {
    detachIndicator();
    return;
  }
  if (!indicator) {
    indicator = document.createElement("li");
    indicator.className = "drop-indicator";
    indicator.setAttribute("aria-hidden", "true");
  }
  const quadrantEl = document.querySelector<HTMLElement>(
    `.quadrant[data-quadrant="${cssEscape(target.quadrant)}"]`,
  );
  const list = quadrantEl?.querySelector<HTMLElement>(".quadrant__list");
  if (!list) {
    detachIndicator();
    return;
  }
  let insertBefore: Element | null = null;
  if (target.beforeTaskId !== null) {
    insertBefore = list.querySelector<HTMLElement>(
      `.task[data-task-id="${cssEscape(target.beforeTaskId)}"]`,
    );
  }
  // insertBefore(node, null) appends to the end.
  list.insertBefore(indicator, insertBefore);
}

function detachIndicator(): void {
  if (indicator?.parentNode) indicator.parentNode.removeChild(indicator);
}

function teardownDrag(): void {
  if (state.phase !== "dragging") return;
  state.ghost.remove();
  const sourceEl = findTaskEl(state.sourceId);
  sourceEl?.classList.remove("task--dragging");
  releasePointer(state.pointerId);
  detachIndicator();
  document.body.classList.remove("body--dragging");
  state = { phase: "idle" };
}

function abort(): void {
  if (state.phase === "pressing" && state.longPressTimer !== null) {
    clearTimeout(state.longPressTimer);
  }
  if (state.phase === "dragging") {
    state.ghost.remove();
    const sourceEl = findTaskEl(state.sourceId);
    sourceEl?.classList.remove("task--dragging");
    releasePointer(state.pointerId);
  }
  detachIndicator();
  document.body.classList.remove("body--dragging");
  state = { phase: "idle" };
}

function releasePointer(pointerId: number): void {
  if (!attached) return;
  try {
    if (attached.hasPointerCapture(pointerId)) {
      attached.releasePointerCapture(pointerId);
    }
  } catch {
    // ignore
  }
}

function findTaskEl(taskId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    `.task[data-task-id="${cssEscape(taskId)}"]`,
  );
}

function suppressNextClick(): void {
  // Time-window flag the row-click handler in matrix.ts reads. We also
  // install a capture-phase listener as belt-and-suspenders: it catches
  // any click that still bubbles to document (e.g. on the live drop
  // target) and stops it before it reaches any row's own listener.
  suppressClicksUntil = performance.now() + CLICK_SUPPRESS_MS;
  const handler = (e: MouseEvent): void => {
    if (!isDragSuppressingClicks()) {
      cleanup();
      return;
    }
    e.stopPropagation();
    e.preventDefault();
  };
  const cleanup = (): void => {
    document.removeEventListener("click", handler, true);
    clearTimeout(timer);
  };
  document.addEventListener("click", handler, true);
  const timer = setTimeout(cleanup, CLICK_SUPPRESS_MS + 50);
}

function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/(["\\])/g, "\\$1");
}
